import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	ReadonlyRefObject,
	Ref,
	forwardRef,
	memo,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import { TreeAccordion } from "../TreeAccordion/TreeAccordion";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	getPropertyPathResolver,
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	DragOverEventArgs,
	DragOverEventResult,
	DropEventArgs,
	useSynchronizedRef,
	useValueEqualMemo
} from "client/Hooks";
import { ActionAndOthers } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { Navigation, NavigationOptions } from "client/Navigation";
import { FontIconName } from "../Icon/Icon";
import { Coordinate } from "client/Misc/commonTypes";

const MAXIMUM_INDENTION_LEVEL: number = 6;

export type TreeViewActionsAndContext<TContext> = {
	actions: readonly ActionAndOthers<TContext>[];
	context: TContext;
};

export type TreeViewItem<TValue> = {
	value: TValue;
	content: string | ReactElement | (() => ReactElement);
	actions?: TreeViewActionsAndContext<any>;
	title?: string;
	isDisabled?: boolean;
	children?: (readonly TreeViewItem<TValue>[]) | null | "lazy";
	icon?: FontIconName;
} & (
	{
		navigation: Navigation | string;
		navigationOptions?: NavigationOptions;
	}
	|
	{
		navigation?: undefined;
		navigationOptions?: undefined;
	}
);

export type TreeViewProps<TValue> = {
	value: TValue | nil;
	onChange?(value: TValue): void;
	onToggleExpand?(item: TValue): void;
	items: readonly TreeViewItem<TValue>[];
	areContainersSelectable?: boolean;
	indentionLevelOffset?: number;
	isCollapsed?: boolean;
	initiallyExpandedNodes?: readonly TValue[];
	trackingId?: string;
	onItemClick?(item: TValue): void;
	onContextMenu?(item: TValue, position: Coordinate): void;
} & (
	TValue extends string | number
		? {
			compareBy?: undefined;
		}
		: {
			compareBy: ((value: TValue) => string | number) | PropertyPath<TValue, string | number>;
		}
) & (
	{
		onDragOver(item: TValue, args: DragOverEventArgs): DragOverEventResult;
		onDrop?(item: TValue, args: DropEventArgs): void;
		shouldAutoExpandOnLongDragOver?(item: TValue): boolean;
	}
	|
	{
		onDragOver?: undefined;
		onDrop?: undefined;
		shouldAutoExpandOnLongDragOver?: undefined;
	}
);

export type TreeViewHandle<TValue> = {
	expandNodes(
		values: readonly TValue[]
	): void;
	toggleNodes(
		values: readonly TValue[],
		action?: ToggleAction
	): void;
	scrollIntoView: (value: TValue) => void;
};

const TreeViewInternal = forwardRef(
	<TValue,>(
		props: TreeViewProps<TValue>,
		ref: Ref<TreeViewHandle<TValue>>
	): ReactElement => {
		const {
			value,
			onChange,
			onToggleExpand,
			items,
			compareBy,
			areContainersSelectable = false,
			indentionLevelOffset = 0,
			isCollapsed = false,
			initiallyExpandedNodes = EMPTY_ARRAY,
			trackingId,
			onItemClick,
			onDragOver,
			onDrop,
			shouldAutoExpandOnLongDragOver,
			onContextMenu
		} = props;

		const onToggleExpandRef: ReadonlyRefObject<typeof onToggleExpand> = useSynchronizedRef(onToggleExpand);
		const itemRefMap: ReadonlyRefObject<Map<string | number, HTMLElement>> = useRef<Map<string | number, HTMLElement>>(new Map());

		const resolveValueUniqueKey = useMemo(
			(): ((value: TValue) => string | number) => {
				if (typeof compareBy === "function") {
					return compareBy;
				}

				if (typeof compareBy === "string") {
					return getPropertyPathResolver(compareBy);
				}

				return value => value as (string | number);
			},
			[compareBy]
		);

		const [expandedItems, setExpandedItems] = useState<Set<string | number>>(
			(): Set<string | number> => new Set(initiallyExpandedNodes.map(resolveValueUniqueKey))
		);

		const valueKey = useMemo(
			(): string | number | null => {
				if (isNil(value)) {
					return null;
				}

				return resolveValueUniqueKey(value);
			},
			[resolveValueUniqueKey, value]
		);

		const toggleExpandHandler = useCallback(
			(
				items: readonly ValueAndKey<TValue>[],
				action: ToggleAction
			): void => {
				for (const item of items) {
					onToggleExpandRef.current
						?.(item.value);
				}

				setExpandedItems(expandedItems => {
					const copy: Set<string | number> = new Set(expandedItems);
					let hasChanges: boolean = false;

					for (const item of items) {
						const itemKey: string | number = item.key;

						switch (action) {
							case "expand":
								if (!copy.has(itemKey)) {
									copy.add(itemKey);
									hasChanges = true;
								}
								break;
							case "collapse":
								if (copy.delete(itemKey)) {
									hasChanges = true;
								}
								break;
							case "toggle":
								if (!copy.delete(itemKey)) {
									copy.add(itemKey);
								}

								hasChanges = true;
								break;
							default:
								never(action);
						}
					}

					return hasChanges
						? copy
						: expandedItems;
				});
			},
			[onToggleExpandRef]
		);

		const toggleItemExpandHandler = useCallback(
			(
				itemKey: string | number,
				item: TValue,
				action: ToggleAction = "toggle"
			): void => {
				toggleExpandHandler(
					[
						{
							key: itemKey,
							value: item
						}
					],
					action
				);
			},
			[toggleExpandHandler]
		);

		const nodeRefHandler = useCallback(
			(itemKey: string | number, ref: HTMLElement | null) => {
				const { current: map } = itemRefMap;

				if (ref) {
					map.set(itemKey, ref);
				} else {
					map.delete(itemKey);
				}
			},
			[]
		);

		const renderRootItems = (indentionLevel: number): ReactElement => {
			const hasAnyItemsWithChildren: boolean = items
				.some((x) => x.children === "lazy" || (x.children?.length ?? 0) > 0);

			return (
				<>
					{items.map(
						(item: TreeViewItem<TValue>) => {
							const itemKey: string | number = resolveValueUniqueKey(item.value);

							return (
								<MemoizedTreeViewNode
									onChange={onChange}
									getItemKey={resolveValueUniqueKey}
									key={itemKey}
									activeItemKey={valueKey}
									onToggle={toggleItemExpandHandler}
									expandedItemKeys={expandedItems}
									item={item}
									itemKey={itemKey}
									indentionLevel={indentionLevel}
									areContainersSelectable={areContainersSelectable}
									hasSiblingsWithChildren={hasAnyItemsWithChildren}
									onClick={onItemClick}
									icon={item.icon}
									onDragOver={onDragOver}
									onDrop={onDrop}
									shouldAutoExpandOnLongDragOver={shouldAutoExpandOnLongDragOver}
									onContextMenu={onContextMenu}
									onRef={nodeRefHandler}
								/>
							);
						}
					)}
				</>
			);
		};

		useImperativeHandle(
			ref,
			(): TreeViewHandle<TValue> => ({
				expandNodes(values: readonly TValue[]): void {
					this.toggleNodes(values, "expand");
				},
				toggleNodes(
					values: readonly TValue[],
					action: ToggleAction = "toggle"
				): void {
					toggleExpandHandler(
						values.map((value: TValue): ValueAndKey<TValue> => ({
							key: resolveValueUniqueKey(value),
							value
						})),
						action
					);
				},
				scrollIntoView: (value: TValue) => {
					const itemKey: string | number = resolveValueUniqueKey(value);
					itemRefMap.current
						.get(itemKey)
						?.scrollIntoView();
				}
			}),
			[resolveValueUniqueKey, toggleExpandHandler]
		);

		return (
			<>
				{indentionLevelOffset > 0
					? (
						<TreeAccordion.SubTree
							isCollapsed={isCollapsed}
							trackingId={trackingId}
						>
							{renderRootItems(indentionLevelOffset)}
						</TreeAccordion.SubTree>
					)
					: (
						<TreeAccordion
							trackingId={trackingId}
						>
							{renderRootItems(0)}
						</TreeAccordion>
					)
				}
			</>
		);
	}
);
TreeViewInternal.displayName = "TreeView";

type TreeViewType = {
	<TValue>(props: TreeViewProps<TValue> & {ref?: Ref<TreeViewHandle<TValue>>; }): ReactElement;
	displayName: string;
};
export const TreeView = TreeViewInternal as unknown as TreeViewType;

type TreeViewNodeProps<TValue> = {
	onChange?(value: TValue): void;
	getItemKey(value: TValue): string | number;
	activeItemKey: string | number | null;
	expandedItemKeys: ReadonlySet<string | number>;
	item: TreeViewItem<TValue>;
	hasSiblingsWithChildren: boolean;
	itemKey: string | number;
	onToggle(
		itemKey: string | number,
		value: TValue,
		action?: ToggleAction
	): void;
	indentionLevel: number;
	areContainersSelectable: boolean;
	onClick?(item: TValue): void;
	icon?: FontIconName;
	onDragOver?(item: TValue, args: DragOverEventArgs): DragOverEventResult;
	onDrop?(item: TValue, args: DropEventArgs): void;
	shouldAutoExpandOnLongDragOver?(item: TValue): boolean;
	onContextMenu?(item: TValue, position: Coordinate): void;
	onRef: (
		itemKind: string | number,
		ref: HTMLElement | null
	) => void;
};

const TreeViewNode = <TValue,>(props: TreeViewNodeProps<TValue>): ReactElement => {
	const {
		onChange,
		getItemKey,
		expandedItemKeys: parentExpandedItemKeys,
		item,
		hasSiblingsWithChildren,
		itemKey,
		activeItemKey: parentActiveItemKey,
		onToggle,
		indentionLevel,
		areContainersSelectable,
		onClick,
		icon,
		onDragOver,
		onDrop,
		shouldAutoExpandOnLongDragOver,
		onContextMenu,
		onRef
	} = props;

	const [activeItemKey, unstableExpandedItemKeys] = useMemo(
		(): [activeItemKey: string | number | null, expandedItemKeys: ReadonlySet<string | number>] => {
			if (parentActiveItemKey === null && parentExpandedItemKeys.size === 0) {
				return [parentActiveItemKey, parentExpandedItemKeys];
			}

			const subKeys: ReadonlySet<string | number> = getSubKeys(item, getItemKey);

			const activeItemKey: string | number | null = isNotNil(parentActiveItemKey) && subKeys.has(parentActiveItemKey)
				? parentActiveItemKey
				: null;

			const expandedItemKeys = new Set([...parentExpandedItemKeys].filter(k => subKeys.has(k)));

			return [activeItemKey, expandedItemKeys];
		},
		[
			getItemKey,
			item,
			parentActiveItemKey,
			parentExpandedItemKeys
		]
	);

	const dragOverHandler = useMemo(
		(): ((args: DragOverEventArgs) => DragOverEventResult) | undefined => {
			if (!onDragOver) {
				return undefined;
			}

			return (args) => onDragOver(item.value, args);
		},
		[item.value, onDragOver]
	);

	const dropHandler = useMemo(
		(): ((args: DropEventArgs) => void) | undefined => {
			if (!onDrop) {
				return undefined;
			}

			return (args) => onDrop(item.value, args);
		},
		[item.value, onDrop]
	);

	const longDragOverHandler = useMemo(
		(): (() => void) | undefined => {
			if (!shouldAutoExpandOnLongDragOver) {
				return undefined;
			}

			return (): void => {
				if (!shouldAutoExpandOnLongDragOver(item.value)) {
					return;
				}

				onToggle(
					itemKey,
					item.value,
					"expand"
				);
			};
		},
		[
			item.value,
			itemKey,
			onToggle,
			shouldAutoExpandOnLongDragOver
		]
	);

	const expandedItemKeys = useValueEqualMemo(
		() => unstableExpandedItemKeys,
		[unstableExpandedItemKeys]
	);

	const title: string | undefined = item.title
		?? (
			typeof item.content === "string"
				? item.content
				: undefined
		);

	const hasNoChildren: boolean = isNil(item.children);

	const {
		isActive,
		isExpanded,
		isSelectable
	} = useMemo(
		(): TreeViewNodeComputedState => {
			const isSelectable: boolean = hasNoChildren || areContainersSelectable;

			const isActive: boolean | undefined = isSelectable
				? itemKey === activeItemKey
				: undefined;

			const isExpanded: boolean = expandedItemKeys.has(itemKey);

			return {
				isActive,
				isExpanded,
				isSelectable
			};
		},
		[
			areContainersSelectable,
			activeItemKey,
			expandedItemKeys,
			hasNoChildren,
			itemKey
		]
	);

	const clickHandler = (): void => {
		if (!isSelectable) {
			onToggle(itemKey, item.value);
		} else {
			onClick?.(item.value);
			onChange?.(item.value);
		}
	};

	const contextMenuHandler = useMemo(
		(): ((position: Coordinate) => void) | undefined => {
			if (!onContextMenu) {
				return;
			}

			return (position: Coordinate): void => {
				onContextMenu(item.value, position);
			};
		},
		[item.value, onContextMenu]
	);

	const renderAction = (): ReactElement | undefined => {
		if (!item.actions) {
			return undefined;
		}

		const {
			actions,
			context
		} = item.actions;

		return (
			<TreeAccordion.ActionList
				actions={actions}
				context={context}
				menuTitle={title ?? localize("_Actions")}
			/>
		);
	};

	const hasAnyItemsWithChildren: boolean = Array.isArray(item.children)
		&& (item.children
			.some((x) => x.children === "lazy" || (x.children?.length ?? 0) > 0) ?? false);

	return (
		<TreeAccordion.Node>
			<TreeAccordion.Item
				{...(
					isNotNil(item.children) && (item.children === "lazy" || item.children.length > 0)
						? {
							expansionToggle: "visible",
							isExpanded,
							onToggleExpansion: () => onToggle(itemKey, item.value)
						}:
						{
							expansionToggle: hasSiblingsWithChildren ? "hidden" : "none"
						}
				)}
				{...(
					item.navigation
						? {
							navigation: item.navigation,
							navigationOptions: item.navigationOptions
						}
						: {}
				)}
				indentionLevel={
					Math.min(
						MAXIMUM_INDENTION_LEVEL,
						indentionLevel
					)
				}
				isSelected={isActive}
				onClick={clickHandler}
				title={title}
				isDisabled={item.isDisabled}
				trackingId={generateTrackingId("tree-view-item", `|${itemKey}`)}
				action={renderAction()}
				icon={icon}
				onDragOver={dragOverHandler}
				onLongDragOver={longDragOverHandler}
				onDrop={dropHandler}
				onContextMenu={contextMenuHandler}
				ref={(x) => onRef(itemKey, x)}
			>
				{typeof item.content === "function"
					? item.content()
					: item.content
				}
			</TreeAccordion.Item>
			{(isNotNil(item.children) && (item.children === "lazy" || item.children.length > 0)) && (
				<TreeAccordion.SubTree
					trackingId={
						generateTrackingId(
							generateTrackingId("tree-view-item", `|${itemKey}`),
							"__children"
						)
					}
					isCollapsed={!isExpanded}
				>
					{Array.isArray(item.children) && item.children.map(
						c => {
							const itemKey: string | number = getItemKey(c.value);

							return (
								<MemoizedTreeViewNode
									item={c}
									itemKey={itemKey}
									getItemKey={getItemKey}
									activeItemKey={activeItemKey}
									expandedItemKeys={expandedItemKeys}
									indentionLevel={indentionLevel + 1}
									onChange={onChange}
									onToggle={onToggle}
									key={itemKey}
									areContainersSelectable={areContainersSelectable}
									hasSiblingsWithChildren={hasAnyItemsWithChildren}
									onClick={onClick}
									onContextMenu={onContextMenu}
									onDragOver={onDragOver}
									onDrop={onDrop}
									shouldAutoExpandOnLongDragOver={shouldAutoExpandOnLongDragOver}
									onRef={onRef}
								/>
							);
						}
					)}
				</TreeAccordion.SubTree>
			)}
		</TreeAccordion.Node>
	);
};
TreeViewNode.displayName = "TreeViewNode";

function getSubKeys<TValue>(
	item: TreeViewItem<TValue>,
	getItemKey: (value: TValue) => string | number
): ReadonlySet<string | number> {
	const result = new Set<string | number>();

	result.add(getItemKey(item.value));
	if (isNotNil(item.children) && item.children !== "lazy") {
		for (const child of item.children) {
			const childSubKeys: ReadonlySet<string | number> = getSubKeys(child, getItemKey);

			childSubKeys.forEach(
				k => result.add(k)
			);
		}
	}

	return result;
}

const MemoizedTreeViewNode = memo(TreeViewNode);

type TreeViewNodeComputedState = {
	isActive: boolean | undefined;
	isSelectable: boolean;
	isExpanded: boolean;
};

type ToggleAction = "toggle" | "expand" | "collapse";

type ValueAndKey<TValue> = {
	value: TValue;
	key: string | number;
};